import { useCallback, useEffect, useMemo, useState } from 'react';
import { UmsatzWert } from '@/components/UmsatzSperre';
import { Link } from 'react-router-dom';
import { salesTrackingAPI, authAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Target, RefreshCw, Loader2, Download, Users, CalendarClock, UserCheck, Handshake, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SalesNachtragen from '@/components/SalesNachtragen';

/* Sales Tracking (Bauauftrag 24.09.2026, Teil A): Wie viele Probetrainings
 * werden zu Verträgen — je Zeitraum, je Berater, je Herkunft. FBC-Ziel ≥ 70 %.
 *
 * Definitionen sind fest im Backend (kennzahlen/vertrieb.py). Die Seite zeigt
 * nur an: Trichter, Abschlussquote mit Ampel, Tabellen, Verlustgründe, Linien.
 * Ein Berater sieht nur seine eigenen Werte — das erzwingt der Server.
 */

const AMPEL = {
  gruen: { text: 'text-emerald-600', bg: 'bg-emerald-500', rahmen: 'border-emerald-300', label: 'Ziel erreicht' },
  gelb: { text: 'text-amber-600', bg: 'bg-amber-500', rahmen: 'border-amber-300', label: 'unter Ziel' },
  rot: { text: 'text-red-600', bg: 'bg-red-500', rahmen: 'border-red-300', label: 'deutlich unter Ziel' },
};

function quote(v) { return v == null ? '—' : `${v.toLocaleString('de-DE')} %`; }
function euro(v) { return v == null ? '—' : `${v.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`; }
function lokalIso(d) {
  // Kalendertag in Ortszeit (nicht UTC), sonst rutscht der Monatsanfang um einen Tag
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* Zeitraum-Vorwahlen: aktueller Monat (Standard), Vormonat, letzte 90 Tage, frei */
function vorwahlen() {
  const heute = new Date();
  const monatStart = new Date(heute.getFullYear(), heute.getMonth(), 1);
  const vormonatStart = new Date(heute.getFullYear(), heute.getMonth() - 1, 1);
  const vormonatEnde = new Date(heute.getFullYear(), heute.getMonth(), 0);
  const vor90 = new Date(heute); vor90.setDate(vor90.getDate() - 90);
  return [
    { key: 'monat', label: 'Dieser Monat', von: lokalIso(monatStart), bis: lokalIso(heute) },
    { key: 'vormonat', label: 'Vormonat', von: lokalIso(vormonatStart), bis: lokalIso(vormonatEnde) },
    { key: '90', label: 'Letzte 90 Tage', von: lokalIso(vor90), bis: lokalIso(heute) },
    { key: 'frei', label: 'Frei wählen' },
  ];
}

export default function SalesTrackingPage() {
  const { userPermissions, isAdminPermissions } = useAuth();
  const leitung = isAdminPermissions || !!userPermissions?.['vertrieb.kennzahlen'];
  const VORWAHLEN = useMemo(vorwahlen, []);
  const [vorwahl, setVorwahl] = useState('monat');
  const [von, setVon] = useState(VORWAHLEN[0].von);
  const [bis, setBis] = useState(VORWAHLEN[0].bis);
  const [berater, setBerater] = useState('');
  const [linie, setLinie] = useState('');
  const [staff, setStaff] = useState([]);
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [exportiert, setExportiert] = useState(false);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const res = await salesTrackingAPI.kennzahlen({ von, bis, berater: berater || undefined, linie: linie || undefined });
      setDaten(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kennzahlen konnten nicht geladen werden');
    } finally {
      setLaedt(false);
    }
  }, [von, bis, berater, linie]);

  useEffect(() => { laden(); }, [laden]);
  useEffect(() => {
    if (leitung) authAPI.users().then(r => setStaff(r.data || [])).catch(() => {});
  }, [leitung]);

  const waehleVorwahl = (v) => {
    setVorwahl(v.key);
    if (v.von) { setVon(v.von); setBis(v.bis); }
  };

  const csv = async () => {
    setExportiert(true);
    try {
      const res = await salesTrackingAPI.exportCsv({ von, bis, berater: berater || undefined, linie: linie || undefined });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = `sales-tracking_${von}_${bis}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export fehlgeschlagen');
    } finally {
      setExportiert(false);
    }
  };

  if (laedt && !daten) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!daten) return null;

  const t = daten.trichter;
  const aq = daten.abschlussquote;
  const ampel = AMPEL[aq.ampel] || null;
  const ziel = daten.ziel?.abschlussquote ?? 70;

  const TRICHTER = [
    { icon: Users, label: 'Leads', wert: t.leads, unter: 'neu im Zeitraum' },
    { icon: CalendarClock, label: 'Termine', wert: t.termine, unter: `${quote(t.quote_termin)} der Leads${t.abgesagt ? ` · ${t.abgesagt} abgesagt` : ''}` },
    { icon: UserCheck, label: 'Erschienen', wert: t.erschienen, unter: `${quote(t.quote_erschienen)} der Termine · No-Show ${quote(t.no_show_quote)}${t.offen ? ` · ${t.offen} offen` : ''}` },
    { icon: Handshake, label: 'Abschlüsse', wert: t.abschluesse, unter: `aus Probetrainings · ${t.vertraege ?? 0} Verträge insgesamt · ${t.bedenkzeit} Bedenkzeit · ${t.kein_interesse} kein Interesse` },
  ];

  const Zeile = ({ z, name }) => (
    <tr className="border-b border-border/40 last:border-b-0">
      <td className="px-3 py-2 font-medium">{name}</td>
      <td className="px-3 py-2 text-right tabular-nums">{z.erschienen}</td>
      <td className="px-3 py-2 text-right tabular-nums font-bold">{z.abschluesse}</td>
      <td className={`px-3 py-2 text-right tabular-nums font-bold ${AMPEL[z.ampel]?.text || 'text-muted-foreground'}`}>{quote(z.abschlussquote)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{quote(z.tag1_quote)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{z.vertraege ?? 0}</td>
      <td className="px-3 py-2 text-right tabular-nums">{daten.umsatz_gesperrt ? <UmsatzWert wert={null} kurz /> : euro(z.vertragswert_schnitt)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{quote(z.anteil_24m)}</td>
    </tr>
  );

  const maxVerlust = Math.max(1, ...daten.verlustgruende.map(g => g.anzahl));

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="sales-tracking-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Target className="w-6 h-6 text-marke" /> Sales Tracking</h1>
          <p className="text-sm text-muted-foreground">
            Wie viele Probetrainings werden zu Verträgen. Zeitraum {daten.zeitraum.von} bis {daten.zeitraum.bis}.
            {!leitung && ' Du siehst deine eigenen Werte.'}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={laden} disabled={laedt} title="Neu laden"><RefreshCw className={`w-4 h-4 ${laedt ? 'animate-spin' : ''}`} /></Button>
          <Button variant="outline" size="sm" onClick={csv} disabled={exportiert} data-testid="st-export"><Download className="w-4 h-4 mr-1" /> CSV</Button>
        </div>
      </div>

      {/* Zeitraum + Filter */}
      <Card><CardContent className="p-3 flex flex-wrap items-center gap-2">
        {VORWAHLEN.map(v => (
          <button key={v.key} onClick={() => waehleVorwahl(v)} data-testid={`st-zeitraum-${v.key}`}
            className={`px-3 py-1.5 rounded-lg border text-xs font-bold ${vorwahl === v.key ? 'border-marke text-marke bg-marke/5' : 'border-border text-muted-foreground hover:bg-muted'}`}>
            {v.label}
          </button>
        ))}
        {vorwahl === 'frei' && (
          <span className="flex items-center gap-1.5 text-xs">
            <Input type="date" value={von} onChange={e => setVon(e.target.value)} className="h-8 w-36" />
            bis
            <Input type="date" value={bis} onChange={e => setBis(e.target.value)} className="h-8 w-36" />
          </span>
        )}
        <span className="flex-1" />
        <select value={linie} onChange={e => setLinie(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="st-linie">
          <option value="">Kinder + Erwachsene</option>
          <option value="erwachsene">Nur Erwachsene</option>
          <option value="kinder">Nur Kinder</option>
        </select>
        {leitung && (
          <select value={berater} onChange={e => setBerater(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="st-berater">
            <option value="">Alle Berater</option>
            {staff.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
          </select>
        )}
      </CardContent></Card>

      {daten.zeitraum.beschnitten && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          <Info className="w-4 h-4 flex-none mt-0.5" />
          <span>Die Auswertung beginnt am Stichtag {daten.zeitraum.stichtag}. Frühere Probetrainings tragen kein Ergebnis und werden nicht geschätzt.</span>
        </div>
      )}

      <SalesNachtragen nachtragen={daten.nachtragen} onGeaendert={laden} />

      {t.ohne_ergebnis > 0 && !(daten.nachtragen?.ohne_ergebnis || []).length && (
        <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground" data-testid="st-ohne-ergebnis">
          <Info className="w-4 h-4 flex-none mt-0.5" />
          <span>{t.ohne_ergebnis} erschienene Probetrainings ohne Ergebnis im Zeitraum. Sie zählen als erschienen, aber nicht als Abschluss und drücken die Quote. Ergebnis bitte unter <Link to="/probetrainings" className="underline">Probetrainings</Link> nachtragen (Vertrag / Bedenkzeit / Kein Interesse).</span>
        </div>
      )}

      {/* Abschlussquote mit Ampel + Trichter */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <Card className={`md:col-span-1 border-2 ${ampel?.rahmen || 'border-border'}`} data-testid="st-abschlussquote">
          <CardContent className="p-4 flex flex-col justify-between h-full">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><Target className="w-3.5 h-3.5" /> Abschlussquote</div>
            <div className={`text-4xl font-extrabold tabular-nums ${ampel?.text || 'text-muted-foreground'}`}>{quote(aq.wert)}</div>
            <div className="text-[11px] text-muted-foreground">
              {aq.erschienen ? `${aq.abschluesse} von ${aq.erschienen} erschienenen` : 'noch kein erschienenes Probetraining'}
            </div>
            <div className="mt-2 flex items-center gap-1.5 text-[11px]">
              <span className={`inline-block w-2.5 h-2.5 rounded-full ${ampel?.bg || 'bg-muted-foreground/40'}`} />
              <span className={ampel?.text || 'text-muted-foreground'}>{ampel?.label || 'keine Datenbasis'}</span>
              <span className="text-muted-foreground">· Ziel ≥ {ziel} %</span>
            </div>
          </CardContent>
        </Card>
        {TRICHTER.map(k => (
          <Card key={k.label} data-testid={`st-${k.label.toLowerCase()}`}><CardContent className="p-4">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><k.icon className="w-3.5 h-3.5" /> {k.label}</div>
            <div className="text-2xl font-extrabold tabular-nums">{k.wert}</div>
            <div className="text-[10px] text-muted-foreground">{k.unter}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Kinder / Erwachsene */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {['erwachsene', 'kinder'].map(l => {
          const z = daten.linien[l];
          const a = AMPEL[z.ampel];
          return (
            <Card key={l} data-testid={`st-linie-${l}`}><CardContent className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold">{l === 'kinder' ? 'Kinder & Jugend' : 'Erwachsene'}</h3>
                <span className={`text-lg font-extrabold tabular-nums ${a?.text || 'text-muted-foreground'}`}>{quote(z.abschlussquote)}</span>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2 text-center">
                {[['Leads', z.leads], ['Termine', z.termine], ['Erschienen', z.erschienen], ['Abschlüsse', z.abschluesse]].map(([n, w]) => (
                  <div key={n}><div className="text-lg font-bold tabular-nums">{w}</div><div className="text-[10px] text-muted-foreground">{n}</div></div>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground mt-2">No-Show {quote(z.no_show_quote)} · Tag 1 {quote(z.tag1_quote)} · {z.vertraege ?? 0} Verträge · Ø {daten.umsatz_gesperrt ? <UmsatzWert wert={null} kurz /> : euro(z.vertragswert_schnitt)} · 24 M {quote(z.anteil_24m)}</div>
            </CardContent></Card>
          );
        })}
      </div>

      {/* Je Berater */}
      <Card><CardContent className="p-0">
        <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
          <h2 className="text-sm font-bold">Je Berater</h2>
          <span className="text-xs text-muted-foreground">Berater = wer den Vertrag abgeschlossen hat, sonst wer Status/Ergebnis eingetragen hat</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Berater</th>
                <th className="px-3 py-2 text-right font-semibold">Erschienen</th>
                <th className="px-3 py-2 text-right font-semibold">Abschlüsse</th>
                <th className="px-3 py-2 text-right font-semibold">Quote</th>
                <th className="px-3 py-2 text-right font-semibold">Tag 1</th>
                <th className="px-3 py-2 text-right font-semibold" title="alle neuen Verträge, auch ohne Probetraining">Verträge</th>
                <th className="px-3 py-2 text-right font-semibold">Ø Vertragswert</th>
                <th className="px-3 py-2 text-right font-semibold">24 Monate</th>
              </tr>
            </thead>
            <tbody>
              {daten.berater.length === 0
                ? <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Noch keine Probetrainings im Zeitraum.</td></tr>
                : daten.berater.map(z => <Zeile key={z.user_id} z={z} name={z.name} />)}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* Je Herkunft + Verlustgründe */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card><CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border/60">
            <h2 className="text-sm font-bold">Je Herkunft</h2>
            <span className="text-xs text-muted-foreground">Kampagne, sonst Quelle, sonst Kanal</span>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Herkunft</th>
                <th className="px-3 py-2 text-right font-semibold">Leads</th>
                <th className="px-3 py-2 text-right font-semibold">Termine</th>
                <th className="px-3 py-2 text-right font-semibold">Erschienen</th>
                <th className="px-3 py-2 text-right font-semibold">Abschlüsse</th>
                <th className="px-3 py-2 text-right font-semibold">Quote</th>
              </tr>
            </thead>
            <tbody>
              {daten.herkunft.length === 0
                ? <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Noch keine Daten.</td></tr>
                : daten.herkunft.map(z => (
                  <tr key={z.schluessel} className="border-b border-border/40 last:border-b-0">
                    <td className="px-3 py-2 font-medium">{z.schluessel}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{z.leads}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{z.termine}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{z.erschienen}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold">{z.abschluesse}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${AMPEL[z.ampel]?.text || 'text-muted-foreground'}`}>{quote(z.abschlussquote)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </CardContent></Card>

        <Card><CardContent className="p-4">
          <h3 className="text-sm font-bold mb-1">Verlustgründe</h3>
          <p className="text-[10px] text-muted-foreground mb-3">Aus „kein Interesse" und nachgetragenen Bedenkzeiten.</p>
          {daten.verlustgruende.every(g => !g.anzahl)
            ? <p className="text-xs text-muted-foreground">Noch kein Verlustgrund im Zeitraum.</p>
            : daten.verlustgruende.map(g => (
              <div key={g.grund} className="flex items-center gap-2 mb-1.5" data-testid={`st-verlust-${g.grund}`}>
                <span className="text-xs w-36 flex-none">{g.label}</span>
                <div className="flex-1 h-4 bg-muted rounded overflow-hidden">
                  <div className="h-full bg-marke/70 rounded" style={{ width: `${(g.anzahl / maxVerlust) * 100}%` }} />
                </div>
                <span className="text-xs tabular-nums w-6 text-right font-bold">{g.anzahl}</span>
              </div>
            ))}
          {daten.abschluesse_ohne_probetraining > 0 && (
            <p className="text-[10px] text-muted-foreground mt-3">{daten.abschluesse_ohne_probetraining} Vertrag/Verträge im Zeitraum ohne Probetraining (zählen nicht in die Quote, aber in Ø Vertragswert und 24 Monate).</p>
          )}
        </CardContent></Card>
      </div>

      <p className="text-[10px] text-muted-foreground">
        Stichtag {daten.zeitraum.stichtag}: ab hier zählen Probetrainings und Abschlüsse. Ein Abschluss zählt für das Probetraining, das im Verkaufsrechner (oder unter „Nachtragen“) angegeben wurde, sonst für das letzte erschienene Probetraining desselben Leads innerhalb von 30 Tagen. Probetrainings zählen als Buchung und als Termin am Lead. Ø Vertragswert und 24 Monate rechnen über alle neuen Verträge.
      </p>
    </div>
  );
}
